import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAudience, AUDIENCES } from "@/contexts/AudienceContext";
import { Phone, MessageCircle, ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Stethoscope, Pill, HelpCircle, BookOpen, Heart, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AudienceBadge() {
  const { audience, openSelector } = useAudience();
  const current = AUDIENCES.find(a => a.id === audience);
  return (
    <button
      onClick={openSelector}
      data-testid="change-audience-condition"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-medium hover:bg-accent/20 transition-colors"
    >
      <span>{current?.icon}</span> Viewing as: {current?.label || 'Patient'}
      <span className="text-[10px] opacity-70">(change)</span>
    </button>
  );
}

export default function ConditionDetailPage() {
  const { slug } = useParams();
  const [condition, setCondition] = useState(null);
  const [loading, setLoading] = useState(true);
  const { audience } = useAudience();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/conditions/${slug}`)
      .then(res => { setCondition(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!condition || condition.error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Condition not found.</p>
        <Link to="/conditions"><Button variant="outline">Back to Conditions</Button></Link>
      </div>
    );
  }

  const showStudentContent = audience === 'student' || audience === 'professional';
  const showCaregiverContent = audience === 'caregiver';

  return (
    <div>
      {/* Breadcrumb + Hero */}
      <section className="py-12 sm:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/conditions" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="back-to-conditions">
            <ArrowLeft className="w-4 h-4" /> Back to Conditions
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{condition.category}</span>
            <AudienceBadge />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-4" data-testid="condition-title">
            {condition.name}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl leading-relaxed">{condition.definition}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Symptoms */}
            <section data-testid="symptoms-section">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Symptoms</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {condition.symptoms?.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{s}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Causes */}
            <section data-testid="causes-section">
              <div className="flex items-center gap-2 mb-4">
                <HelpCircle className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Causes</h2>
              </div>
              <ul className="space-y-2">
                {condition.causes?.map((c, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-2" />
                    <span className="text-sm text-muted-foreground">{c}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Diagnosis */}
            <section data-testid="diagnosis-section">
              <div className="flex items-center gap-2 mb-4">
                <Stethoscope className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Diagnosis</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{condition.diagnosis}</p>
            </section>

            {/* Treatment */}
            <section data-testid="treatment-section">
              <div className="flex items-center gap-2 mb-4">
                <Pill className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Treatment Options</h2>
              </div>
              <div className="space-y-2">
                {condition.treatment_options?.map((t, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* When to Seek Help */}
            <section data-testid="when-to-seek-help">
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-6">
                  <h2 className="font-heading text-xl font-semibold mb-3">When to Seek Help</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{condition.when_to_seek_help}</p>
                </CardContent>
              </Card>
            </section>

            {/* STUDENT/PROFESSIONAL: Pathophysiology */}
            {showStudentContent && condition.pathophysiology && (
              <section data-testid="pathophysiology-section" className="border-l-4 border-accent pl-5">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Pathophysiology</h2>
                  <span className="px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium">Detailed</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{condition.pathophysiology}</p>
              </section>
            )}

            {/* STUDENT/PROFESSIONAL: Neuroscience */}
            {showStudentContent && condition.neuroscience && (
              <section data-testid="neuroscience-section" className="border-l-4 border-accent pl-5">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Neuroscience & Brain Circuits</h2>
                  <span className="px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium">Advanced</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{condition.neuroscience}</p>
              </section>
            )}

            {/* STUDENT/PROFESSIONAL: Epidemiology */}
            {showStudentContent && condition.epidemiology && (
              <section data-testid="epidemiology-section" className="border-l-4 border-primary pl-5">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Epidemiology</h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{condition.epidemiology}</p>
              </section>
            )}

            {/* STUDENT/PROFESSIONAL: Differential Diagnosis */}
            {showStudentContent && condition.differential_diagnosis && (
              <section data-testid="differential-section" className="border-l-4 border-primary pl-5">
                <div className="flex items-center gap-2 mb-4">
                  <Stethoscope className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Differential Diagnosis</h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{condition.differential_diagnosis}</p>
              </section>
            )}

            {/* STUDENT/PROFESSIONAL: Assessment Tools */}
            {showStudentContent && condition.clinical_assessment_tools && (
              <section data-testid="tools-section" className="border-l-4 border-primary pl-5">
                <div className="flex items-center gap-2 mb-4">
                  <Stethoscope className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Clinical Assessment Tools</h2>
                </div>
                <ul className="space-y-2">
                  {condition.clinical_assessment_tools.map((tool, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />{tool}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* STUDENT/PROFESSIONAL: Prognosis */}
            {showStudentContent && condition.prognosis && (
              <section data-testid="prognosis-section" className="border-l-4 border-accent pl-5">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Prognosis & Outcomes</h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{condition.prognosis}</p>
              </section>
            )}

            {/* CAREGIVER: Guide */}
            {showCaregiverContent && condition.caregiver_guide && (
              <section data-testid="caregiver-section">
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Caregiver Guide</h2>
                  <span className="px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium">For Family</span>
                </div>
                <div className="space-y-2">
                  {condition.caregiver_guide.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20">
                      <span className="text-accent font-bold shrink-0">{i + 1}.</span>
                      <span className="text-sm text-muted-foreground">{tip}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* PATIENT: Action Plan */}
            {(!audience || audience === 'patient') && condition.patient_action_plan && (
              <section data-testid="action-plan-section">
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Your Action Plan</h2>
                </div>
                <div className="space-y-2">
                  {condition.patient_action_plan.map((step, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50">
                      <span className="text-primary font-bold shrink-0">{i + 1}.</span>
                      <span className="text-sm text-muted-foreground">{step}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <Card className="border sticky top-24">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-heading font-semibold text-lg">Book a Consultation</h3>
                <p className="text-sm text-muted-foreground">
                  Get expert treatment for {condition.name?.toLowerCase()} from Dr. Ambrish Singal, one of the most experienced psychiatrists in the Tricity.
                </p>
                <div className="space-y-3">
                  <a href="tel:+919915096633" className="block" data-testid="sidebar-call">
                    <Button className="w-full gap-2 rounded-full">
                      <Phone className="w-4 h-4" /> Call +91-99150 96633
                    </Button>
                  </a>
                  <a
                    href={`https://wa.me/919815333660?text=Hi%20Dr.%20Ambrish,%20I%20need%20help%20with%20${encodeURIComponent(condition.name)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="block" data-testid="sidebar-whatsapp"
                  >
                    <Button variant="outline" className="w-full gap-2 rounded-full">
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </Button>
                  </a>
                  <Link to="/contact" className="block" data-testid="sidebar-book">
                    <Button variant="secondary" className="w-full gap-2 rounded-full">
                      Book Online <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="pt-4 border-t space-y-2 text-sm text-muted-foreground">
                  <p><strong className="text-foreground">Clinic</strong></p>
                  <p>Opposite Housefed Society 2, 704, HT Towers, Sector 79, Sahibzada Ajit Singh Nagar, Punjab 140308</p>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}
